import {
  forwardRef,
  useCallback,
  useMemo,
  type AnchorHTMLAttributes,
  type MouseEvent,
} from "react";
import { useRouter, useRouterState, type RegisteredRouter } from "@tanstack/react-router";

export interface AppLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
  href: string;
  prefetch?: boolean;
}

const isPlainLeftClick = (event: MouseEvent<HTMLAnchorElement>): boolean =>
  event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

export const AppLink = forwardRef<HTMLAnchorElement, AppLinkProps>(function AppLink(
  { children, href, onClick, onFocus, onMouseEnter, prefetch = true, target, ...props },
  ref,
) {
  const router = useRouter();
  const preload = useCallback(() => {
    if (!prefetch || href.startsWith("http://") || href.startsWith("https://")) return;
    void router.preloadRoute<string, string>({ href });
  }, [href, prefetch, router]);

  return (
    <a
      {...props}
      ref={ref}
      href={href}
      target={target}
      onMouseEnter={(event) => {
        onMouseEnter?.(event);
        preload();
      }}
      onFocus={(event) => {
        onFocus?.(event);
        preload();
      }}
      onClick={(event) => {
        onClick?.(event);
        if (event.defaultPrevented || target === "_blank" || !isPlainLeftClick(event)) return;
        if (href.startsWith("http://") || href.startsWith("https://")) return;
        event.preventDefault();
        void router.navigate<RegisteredRouter, string>({ href });
      }}
    >
      {children}
    </a>
  );
});

export function useAppPathname(): string {
  return useRouterState({ select: (state) => state.location.pathname });
}

export function useAppRouter() {
  const router = useRouter();
  return useMemo(() => ({
    push: (href: string) => {
      void router.navigate<RegisteredRouter, string>({ href });
    },
    replace: (href: string) => {
      void router.navigate<RegisteredRouter, string>({ href, replace: true });
    },
    back: () => router.history.back(),
    refresh: () => router.invalidate(),
    prefetch: (href: string) => router.preloadRoute<string, string>({ href }),
  }), [router]);
}
